/**
 * StockHistoricoRepository - Repositorio unificado para Historial de Stock
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 */

import { v4 as uuidv4 } from 'uuid';
import {
  IStockHistoricoRepository,
  FiltroStockHistorico,
  StockHistoricoPaginatedResult,
  StockHistoricoStatistics
} from '../interfaces/IStockHistoricoRepository';
import { StockHistorico, TipoOperacionStock } from '../interfaces/entities';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';

/**
 * Repositorio unificado de Historial de Stock
 */
export class StockHistoricoRepository extends BaseRepository<StockHistorico, string> implements IStockHistoricoRepository {

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'stocks_historico', 'stocks_historico');

    if (profileRepo) {
      this.setProfileRepository(profileRepo);
    }

    // Habilitar funcionalidades de BaseRepository
    this.supportsSoftDelete = true;
    this.autoTimestamp = true;
  }

  async add(data: Omit<StockHistorico, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    const record: StockHistorico = {
      ...data,
      id,
      updated_at: now,
      empresa_id: data.empresa_id || profile?.empresa_activa_id || '',
      user_id: data.user_id || profile?.user_id || '',
      is_deleted: false
    };

    await this.db.insert('stocks_historico', record);
    this.emitSync('INSERT', record);

    return id;
  }

  async update(data: Partial<StockHistorico> & { id: string }): Promise<void> {
    const updatedData = {
      ...data,
      updated_at: this.getCurrentTimestamp()
    };
    await this.db.update('stocks_historico', data.id, updatedData);
    this.emitSync('UPDATE', updatedData as any);
  }

  async delete(id: string): Promise<void> {
    await this.db.delete('stocks_historico', id);
    this.emitSync('DELETE', id);
  }

  async softDelete(id: string): Promise<void> {
    const updatedData = {
      id,
      is_deleted: true,
      updated_at: this.getCurrentTimestamp()
    };
    await this.db.update('stocks_historico', id, updatedData);
    this.emitSync('SOFT_DELETE', updatedData as any);
  }

  async restore(id: string): Promise<void> {
    const updatedData = { id, is_deleted: false };
    await this.db.update('stocks_historico', id, updatedData);
    this.emitSync('RESTORE', { id, is_deleted: false } as any);
  }

  async getDeleted(): Promise<StockHistorico[]> {
    return this.db.query<StockHistorico>('stocks_historico', {
      field: 'is_deleted',
      operator: 'equals',
      value: true
    });
  }

  async getByFiltros(filtros: FiltroStockHistorico): Promise<StockHistorico[]> {
    // Partimos de los registros de la empresa activa para no mezclar empresas.
    let results = (await this.getAllForActiveEmpresa()).filter(h => !h.is_deleted);

    if (filtros.stock_id) {
      results = results.filter(h => h.stock_id === filtros.stock_id);
    }
    if (filtros.tipo_operacion) {
      results = results.filter(h => h.tipo_operacion === filtros.tipo_operacion);
    }
    if (filtros.ubicacion_id) {
      results = results.filter(h => h.ubicacion_id === filtros.ubicacion_id);
    }

    // Filtros de rango de fechas (requieren lógica adicional)
    if (filtros.fecha_inicio || filtros.fecha_fin) {
      results = results.filter(h => {
        if (filtros.fecha_inicio && h.fecha_evento < filtros.fecha_inicio) return false;
        if (filtros.fecha_fin && h.fecha_evento > filtros.fecha_fin) return false;
        return true;
      });
    }

    return results;
  }

  async getByStockId(stockId: string): Promise<StockHistorico[]> {
    return this.db.queryMultiple<StockHistorico>('stocks_historico', [
      { field: 'stock_id', operator: 'equals', value: stockId },
      { field: 'is_deleted', operator: 'equals', value: false }
    ]);
  }

  async getByPeriodo(fechaInicio: string, fechaFin: string): Promise<StockHistorico[]> {
    // Filtramos por empresa activa para no mezclar datos de otras empresas.
    const all = await this.getAllForActiveEmpresa();
    return all.filter(h => {
      if (h.is_deleted) return false;
      return h.fecha_evento >= fechaInicio && h.fecha_evento <= fechaFin;
    });
  }

  async getByTipoOperacion(tipo: TipoOperacionStock): Promise<StockHistorico[]> {
    // Filtramos por empresa activa para no mezclar datos de otras empresas.
    const all = await this.getAllForActiveEmpresa();
    return all.filter(h => !h.is_deleted && h.tipo_operacion === tipo);
  }

  async getByTipoOperacionAndPeriodo(
    tipo: TipoOperacionStock,
    fechaInicio: string,
    fechaFin: string
  ): Promise<StockHistorico[]> {
    const results = await this.getByTipoOperacion(tipo);
    return results.filter(h =>
      h.fecha_evento >= fechaInicio && h.fecha_evento <= fechaFin
    );
  }

  async getByUbicacion(ubicacionId: string): Promise<StockHistorico[]> {
    return this.db.queryMultiple<StockHistorico>('stocks_historico', [
      { field: 'ubicacion_id', operator: 'equals', value: ubicacionId },
      { field: 'is_deleted', operator: 'equals', value: false }
    ]);
  }

  async getByEmpresa(empresaId: string): Promise<StockHistorico[]> {
    return this.db.queryMultiple<StockHistorico>('stocks_historico', [
      { field: 'empresa_id', operator: 'equals', value: empresaId },
      { field: 'is_deleted', operator: 'equals', value: false }
    ]);
  }

  async getAllPaginated(
    offset: number,
    limit: number,
    filtros?: FiltroStockHistorico,
    sortBy: string = 'fecha_evento',
    sortDirection: 'asc' | 'desc' = 'desc'
  ): Promise<StockHistoricoPaginatedResult> {
    let items = filtros ? await this.getByFiltros(filtros) : await this.getAllForActiveEmpresa();

    // Sort
    items.sort((a, b) => {
      const aVal = (a as any)[sortBy] || '';
      const bVal = (b as any)[sortBy] || '';
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    const total = items.length;
    items = items.slice(offset, offset + limit);

    return {
      items,
      total,
      hasMore: offset + limit < total
    };
  }

  async getStatistics(fechaInicio?: string, fechaFin?: string): Promise<StockHistoricoStatistics> {
    let historico: StockHistorico[];

    if (fechaInicio && fechaFin) {
      historico = await this.getByPeriodo(fechaInicio, fechaFin);
    } else {
      historico = await this.getAllForActiveEmpresa();
    }

    const compras = historico.filter(h => h.tipo_operacion === 'COMPRA');
    const consumos = historico.filter(h => h.tipo_operacion === 'CONSUMO');
    const ventas = historico.filter(h => h.tipo_operacion === 'VENTA');
    const ajustes = historico.filter(h => h.tipo_operacion === 'AJUSTE');

    return {
      totalCompras: compras.length,
      totalConsumos: consumos.length,
      totalVentas: ventas.length,
      totalAjustes: ajustes.length,
      montoTotalCompras: compras.reduce((sum, h) => sum + (h.precio_total || 0), 0)
    };
  }

  async getUltimoMovimiento(stockId: string): Promise<StockHistorico | undefined> {
    const movimientos = await this.getByStockId(stockId);
    if (movimientos.length === 0) return undefined;

    return movimientos.reduce((latest, current) => {
      if (!latest.updated_at) return current;
      if (!current.updated_at) return latest;
      return current.updated_at > latest.updated_at ? current : latest;
    });
  }

  async getByStockIdAndFecha(stockId: string, fecha: string): Promise<StockHistorico[]> {
    const results = await this.getByStockId(stockId);
    return results.filter(h => h.fecha_evento <= fecha);
  }

  async getOperacionesPosteriores(
    stockId: string,
    fechaEvento: string,
    fechaModificacion: string
  ): Promise<StockHistorico[]> {
    const results = await this.getByStockId(stockId);
    return results.filter(h =>
      h.fecha_evento > fechaEvento ||
      (h.fecha_evento === fechaEvento && (h.updated_at || '') > fechaModificacion)
    );
  }

  async getOperacionesAnteriores(
    stockId: string,
    fechaEvento: string,
    fechaModificacion: string
  ): Promise<StockHistorico[]> {
    const results = await this.getByStockId(stockId);
    return results.filter(h =>
      h.fecha_evento < fechaEvento ||
      (h.fecha_evento === fechaEvento && (h.updated_at || '') <= fechaModificacion)
    );
  }

  // ==================== Bulk Operations ====================
  // Estos métodos ahora vienen de BaseRepository

  // ==================== Fin ====================
}
